import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  TextField,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcFormModal from '~/components/General/OlcFormModal';

// Redux Actions
import {
  lookupPhoneNumbers,
  createPhoneTrackingNumber,
  updatePhoneTrackingNumber,
} from '~/redux/actions/phone-tracking-actions';
import { SET_PHONE_TRACKING_LOOKUP_DATA } from '~/redux/actions/action-types';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Constants
import { MESSAGES } from '~/utils/message';
import { formatPhoneNumber } from '~/utils/helperFunctions';

// Styles
import './styles.scss';

const getUsPhoneDigits = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  const withoutCountryCode =
    digits.length > 10 && digits.startsWith('1') ? digits.slice(1) : digits;
  return withoutCountryCode.slice(0, 10);
};

const formatUsPhoneInput = (value = '') => formatPhoneNumber(getUsPhoneDigits(value));

const ActivateTrackingModal = ({
  modalOpen,
  onClose,
  editingId = null,
  onSuccess,
  isCompanyEnabled,
}) => {
  const [formData, setFormData] = useState({
    phoneName: '',
    callForwardingNumber: '',
    smsForwardingEmail: '',
    company: '',
    areaCode: '',
    selectedNumber: '',
  });
  const [hasSearchedNumbers, setHasSearchedNumbers] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const availableNumbersRef = useRef(null);

  const dispatch = useDispatch();

  const phoneTrackingLookupData = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingLookupData
  );
  const phoneTrackingLookupLoader = useSelector(
    (state) => state.phoneTrackingReducers.phoneTrackingLookupLoader
  );
  const editingPhoneNumber = useSelector((state) =>
    editingId
      ? state.phoneTrackingReducers.phoneTrackingNumbers.find((number) => number.id === editingId)
      : null
  );

  const availableNumbers =
    phoneTrackingLookupData.length > 0
      ? phoneTrackingLookupData.map((number) => ({
          phoneLabel: `${formatUsPhoneInput(number.phone_number)} - ${number.city}, ${number.state}`,
          phoneNumber: number.phone_number,
        }))
      : [];

  const companyOptions = ['Company A', 'Company B', 'Company C'].map((title, index) => ({
    id: index + 1,
    title,
  }));

  useEffect(() => {
    if (modalOpen.type === 'edit' && editingPhoneNumber) {
      setFormData({
        phoneName: editingPhoneNumber.name || '',
        callForwardingNumber: formatUsPhoneInput(editingPhoneNumber.forwardingPhoneNumber || ''),
        smsForwardingEmail: editingPhoneNumber.smsForwardingEmail || '',
        company: editingPhoneNumber.company || '',
        areaCode: '',
        selectedNumber: editingPhoneNumber.phoneNumber || '',
      });
      setErrors({});
      setHasSearchedNumbers(false);
    } else if (modalOpen.type === 'activate') {
      setFormData({
        phoneName: '',
        callForwardingNumber: '',
        smsForwardingEmail: '',
        company: '',
        areaCode: '',
        selectedNumber: '',
      });
      setErrors({});
      setHasSearchedNumbers(false);
    }
  }, [modalOpen.type, editingPhoneNumber, modalOpen.open]);

  useEffect(() => {
    if (!hasSearchedNumbers || availableNumbers.length === 0) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      availableNumbersRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [hasSearchedNumbers, availableNumbers.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handlePhoneInputChange = (field, value) => {
    handleInputChange(field, formatUsPhoneInput(value));
  };

  const handleFindNumbers = async () => {
    const trimmedAreaCode = formData.areaCode.trim();
    if (!trimmedAreaCode) {
      setErrors((prev) => ({
        ...prev,
        areaCode: 'Area code is required.',
      }));
      return;
    }

    if (!/^\d+$/.test(trimmedAreaCode)) {
      setErrors((prev) => ({
        ...prev,
        areaCode: 'Enter a valid area code.',
      }));
      return;
    }

    setHasSearchedNumbers(false);

    setErrors((prev) => {
      const { areaCode, ...rest } = prev;
      return rest;
    });
    setFormData((prev) => ({
      ...prev,
      areaCode: trimmedAreaCode,
      selectedNumber: '',
    }));

    try {
      const response = await dispatch(lookupPhoneNumbers({ areaCode: trimmedAreaCode }));
      if (response.status === 200) {
        setHasSearchedNumbers(true);
      } else {
        dispatch(
          failure(response?.data?.message || 'Failed to find available numbers. Please try again.')
        );
      }
    } catch (error) {
      dispatch(failure('Failed to find available numbers. Please try again.'));
    }
  };

  const validateForm = () => {
    const trimmedPhoneName = formData.phoneName.trim();
    const trimmedCallForwardingNumber = getUsPhoneDigits(formData.callForwardingNumber);
    const trimmedSmsForwardingEmail = formData.smsForwardingEmail.trim();
    const trimmedAreaCode = formData.areaCode.trim();
    const validationErrors = {};

    if (!trimmedPhoneName) {
      validationErrors.phoneName = 'Phone name is required.';
    }

    if (!trimmedCallForwardingNumber) {
      validationErrors.callForwardingNumber = 'Call forwarding number is required.';
    } else if (trimmedCallForwardingNumber.length !== 10) {
      validationErrors.callForwardingNumber = 'Please enter a valid 10-digit phone number.';
    }

    if (
      trimmedSmsForwardingEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSmsForwardingEmail)
    ) {
      validationErrors.smsForwardingEmail = 'Please enter a valid email address.';
    }

    if (modalOpen.type === 'activate') {
      if (!trimmedAreaCode) {
        validationErrors.areaCode = 'Please enter an area code and select a phone number.';
      } else if (!/^\d+$/.test(trimmedAreaCode)) {
        validationErrors.areaCode = 'Please enter a valid area code and select a phone number.';
      } else if (!hasSearchedNumbers) {
        validationErrors.areaCode = 'Please search for available numbers first.';
      } else if (hasSearchedNumbers && !formData.selectedNumber) {
        validationErrors.areaCode = 'Please select a phone number.';
      }
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return null;
    }

    return {
      phoneName: trimmedPhoneName,
      callForwardingNumber: trimmedCallForwardingNumber,
      smsForwardingEmail: trimmedSmsForwardingEmail,
      areaCode: trimmedAreaCode,
    };
  };

  const handleActivate = async () => {
    const validatedValues = validateForm();
    if (!validatedValues) {
      return;
    }

    const { phoneName, callForwardingNumber, smsForwardingEmail } = validatedValues;

    setLoading(true);

    try {
      if (modalOpen.type === 'activate') {
        const payload = {
          name: phoneName,
          phoneNumber: formData.selectedNumber,
          forwardingPhoneNumber: callForwardingNumber,
        };
        if (smsForwardingEmail) payload.smsForwardingEmail = smsForwardingEmail;

        const response = await dispatch(createPhoneTrackingNumber(payload));

        if (response.status === 200 || response.status === 201) {
          onSuccess && onSuccess();
          handleCancel();
          dispatch(
            success(response?.data?.message || 'Tracking Phone Number Created Successfully.')
          );
        } else {
          dispatch(
            failure(
              response?.data?.message || 'Failed to save tracking phone number. Please try again.'
            )
          );
        }
      } else if (modalOpen.type === 'edit') {
        const payload = {
          id: editingId,
          updatedName: phoneName,
          forwardingPhoneNumber: callForwardingNumber,
        };
        if (smsForwardingEmail) payload.smsForwardingEmail = smsForwardingEmail;

        // Update existing phone tracking number
        const response = await dispatch(updatePhoneTrackingNumber(payload));

        if (response.status === 200) {
          onSuccess && onSuccess();
          handleCancel();
          dispatch(success('Phone tracking number updated successfully.'));
        } else {
          dispatch(
            failure(
              response?.data?.message || 'Failed to update tracking phone number. Please try again.'
            )
          );
        }
      }
    } catch (error) {
      dispatch(failure('Failed to save phone tracking number. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setTimeout(() => {
      setFormData({
        phoneName: '',
        callForwardingNumber: '',
        smsForwardingEmail: '',
        company: '',
        areaCode: '',
        selectedNumber: '',
      });
      setSelectedCompany([]);
      setErrors({});
      dispatch({ type: SET_PHONE_TRACKING_LOOKUP_DATA, payload: [] });
    }, 10);
  };

  return (
    <OlcFormModal
      open={modalOpen.open}
      onClose={handleCancel}
      title={
        modalOpen.type === 'activate'
          ? 'Activate Tracking Phone Number'
          : modalOpen.type === 'edit'
            ? 'Edit Tracking Phone Number'
            : ''
      }
      className="activateTrackingModalWrapper"
      contentClassName="activateTrackingModalDialogContent"
      dialogProps={{
        classes: {
          paper: `activateTrackingModal ${modalOpen.type === 'edit' ? 'edit' : ''}`,
        },
      }}
      footer={
        <>
          <OlcButton
            variant="secondary"
            onClick={handleCancel}
            className="cancelButton"
            disabled={loading}
          >
            Cancel
          </OlcButton>
          <OlcButton
            variant="primary"
            onClick={handleActivate}
            className="activateButton"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : modalOpen.type === 'activate' ? (
              'Activate Tracking Number'
            ) : modalOpen.type === 'edit' ? (
              'Save'
            ) : (
              ''
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="activateTrackingModalContent">
        <Box className="modalBody">
          <Box className="inputGroup">
            <Typography className="inputLabel">
              Phone Name<span className="olcRequired">*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Phone Name"
              value={formData.phoneName.trimStart()}
              onChange={(e) => handleInputChange('phoneName', e.target.value)}
              variant="outlined"
              className="modalInput"
              required
            />
            {Boolean(errors.phoneName) && (
              <Typography className="errorMessage trackingError">{errors.phoneName}</Typography>
            )}
          </Box>

          <Box className="inputGroup">
            <Typography className="inputLabel">
              Call Forwarding Number<span className="olcRequired">*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Forwarding Phone Number"
              value={formData.callForwardingNumber.trimStart()}
              onChange={(e) => handlePhoneInputChange('callForwardingNumber', e.target.value)}
              variant="outlined"
              className="modalInput"
              required
            />
            <Typography className="fieldHelpText">
              {MESSAGES.PHONE_TRACKING.ACTIVATE_MODAL.CALL_FORWARDING_TOOLTIP}
            </Typography>
            {Boolean(errors.callForwardingNumber) && (
              <Typography className="errorMessage trackingError">
                {errors.callForwardingNumber}
              </Typography>
            )}
          </Box>

          <Box className="inputGroup">
            <Typography className="inputLabel">SMS Email Forwarding</Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter Email Address"
              value={formData.smsForwardingEmail.trimStart()}
              onChange={(e) => handleInputChange('smsForwardingEmail', e.target.value)}
              variant="outlined"
              className="modalInput"
            />
            <Typography className="fieldHelpText">
              {MESSAGES.PHONE_TRACKING.ACTIVATE_MODAL.SMS_EMAIL_TOOLTIP}
            </Typography>
            {Boolean(errors.smsForwardingEmail) && (
              <Typography className="errorMessage trackingError">
                {errors.smsForwardingEmail}
              </Typography>
            )}
          </Box>

          {isCompanyEnabled && (
            <Box className="inputGroup">
              <Typography className="inputLabel">Assign to Company</Typography>
              <OlcDropdownSearch
                className="modalSelect"
                options={companyOptions}
                selectedValue={
                  companyOptions.find((company) => company.title === formData.company) || null
                }
                setSelectedValue={(company) => handleInputChange('company', company?.title || '')}
                placeHolderText="Select Company"
                searchable={false}
                clearable
              />
            </Box>
          )}

          {modalOpen.type === 'activate' && (
            <>
              <Box className="inputGroup">
                <Typography className="inputLabel">
                  Enter an Area Code<span className="olcRequired">*</span>
                </Typography>
                <Typography className="inputDescription">
                  Enter your preferred area code for the tracking number. If it's unavailable, we'll
                  automatically assign the closest available area code based on location.
                </Typography>
                <Box className="areaCodeWrapper">
                  <TextField
                    placeholder="Enter Area Code"
                    value={formData.areaCode}
                    onChange={(e) => handleInputChange('areaCode', e.target.value)}
                    variant="outlined"
                    className="areaCodeInput"
                  />
                  <OlcButton
                    variant="primary"
                    onClick={handleFindNumbers}
                    className="findNumbersButton"
                    disabled={phoneTrackingLookupLoader || !formData.areaCode.trim()}
                  >
                    {phoneTrackingLookupLoader ? 'Finding...' : 'Find Available Numbers'}
                  </OlcButton>
                </Box>
                {Boolean(errors.areaCode) && (
                  <Typography className="errorMessage trackingError">{errors.areaCode}</Typography>
                )}
              </Box>

              {availableNumbers.length > 0 && (
                <Box
                  ref={availableNumbersRef}
                  className="inputGroup phoneNumberGroup"
                  data-testid="available-phone-numbers"
                >
                  <Typography className="inputLabel">
                    Select a phone number<span className="olcRequired">*</span>
                  </Typography>
                  <RadioGroup
                    value={formData.selectedNumber}
                    onChange={(e) => handleInputChange('selectedNumber', e.target.value)}
                    className="phoneNumberOptions"
                  >
                    {availableNumbers.map(({ phoneLabel, phoneNumber }, index) => (
                      <FormControlLabel
                        key={index}
                        value={phoneNumber}
                        control={<Radio disableRipple={true} className="radioButton" />}
                        label={phoneLabel}
                        className="phoneNumberOption"
                      />
                    ))}
                  </RadioGroup>
                </Box>
              )}
            </>
          )}

          {modalOpen.type === 'edit' && (
            <Box className="inputGroup">
              <Typography className="inputLabel">
                Tracking Phone Number<span className="olcRequired">*</span>
              </Typography>
              <TextField
                fullWidth
                disabled={true}
                placeholder="Enter Forwarding Phone Number"
                value={formatUsPhoneInput(formData.selectedNumber)}
                variant="outlined"
                className="modalInput"
                required
              />
            </Box>
          )}
        </Box>

        {availableNumbers.length > 0 && (
          <Typography className="costNote">
            Each additional call tracking number beyond your plan&apos;s limit will add $5 to your
            monthly subscription.
          </Typography>
        )}
      </Box>
    </OlcFormModal>
  );
};

export default ActivateTrackingModal;
